import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogTitle, DialogClose } from "@/components/ui/dialog";
import { Download, FileText, Clock, Lock, Loader2, Eye, ExternalLink, X, FileArchive } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { formatBytes } from "@/lib/secureCrypto";
import { PublicShell } from "@/components/Brand";

const IMG = ["image/png", "image/jpeg", "image/webp", "image/gif"];
const PREVIEW_MAX = 50 * 1024 * 1024;
const ZIP_MAX = 1024 * 1024 * 1024;
const canPreview = (f) => f.size <= PREVIEW_MAX && (IMG.includes(f.content_type) || f.content_type === "application/pdf");

const SenderHeader = ({ token, t }) => (
  <div className="flex items-center gap-4" data-testid="transfer-branding">
    {t.has_logo && <img src={`${API}/public/transfers/${token}/logo`} alt="" className="h-14 w-14 rounded-2xl object-cover border border-border bg-white" data-testid="transfer-sender-logo" />}
    <h1 className="text-4xl sm:text-5xl font-semibold font-display tracking-tight break-words min-w-0" data-testid="transfer-welcome">
      {t.welcome || (t.locked ? "Password needed" : "Files for you")}
    </h1>
  </div>
);

export default function TransferDownload() {
  const { token } = useParams();
  const [t, setT] = useState(null);
  const [error, setError] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    api.get(`/public/transfers/${token}`).then((r) => setT(r.data)).catch((e) => {
      setError(e?.response?.status === 410 ? "This transfer has expired." : "This transfer doesn't exist or was deleted.");
    });
  }, [token]);

  const unlock = async (e) => {
    e.preventDefault(); setBusy(true); setError("");
    try { const { data } = await api.post(`/public/transfers/${token}/unlock`, { password }); setT(data); }
    catch (err) { setError(err?.response?.status === 401 ? "Wrong password — check with the sender." : "Couldn't unlock — try again."); }
    finally { setBusy(false); }
  };

  const fileUrl = (id, inline = false) => {
    const q = new URLSearchParams();
    if (t?.grant) q.set("grant", t.grant);
    if (inline) q.set("inline", "1");
    const qs = q.toString();
    return `${API}/public/transfers/${token}/files/${id}${qs ? `?${qs}` : ""}`;
  };
  const downloadAll = () => t.files.forEach((f, i) => setTimeout(() => {
    const a = document.createElement("a"); a.href = fileUrl(f.id); a.download = f.filename; a.click();
  }, i * 800));

  const [zipping, setZipping] = useState(null);
  const photos = t && !t.locked ? t.files.filter((f) => f.content_type?.startsWith("image/")) : [];
  const photosSize = photos.reduce((s, f) => s + f.size, 0);
  const downloadPhotosZip = async () => {
    setZipping(0);
    try {
      const { default: JSZip } = await import("jszip");
      const zip = new JSZip();
      const used = new Set();
      for (let i = 0; i < photos.length; i++) {
        const f = photos[i];
        const res = await fetch(fileUrl(f.id));
        if (!res.ok) throw new Error(`download failed (${res.status})`);
        let name = f.filename;
        for (let n = 1; used.has(name); n++) name = f.filename.replace(/(\.[^.]*)?$/, ` (${n})$1`);
        used.add(name);
        zip.file(name, await res.blob(), { binary: true });
        setZipping(Math.round(((i + 1) / photos.length) * 100));
      }
      const blob = await zip.generateAsync({ type: "blob", compression: "STORE" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `${(t.welcome || "photos").replace(/[^\w\- ]+/g, "").trim() || "photos"}.zip`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (e) {
      toast.error("Couldn't build the zip — try downloading the photos one by one");
      console.warn("[zip]", e);
    } finally { setZipping(null); }
  };

  const images = t && !t.locked ? t.files.filter((f) => canPreview(f) && IMG.includes(f.content_type)) : [];

  return (
    <PublicShell testId="transfer-page">
      {t?.locked && (
        <form onSubmit={unlock} className="max-w-md space-y-4" data-testid="transfer-locked">
          <SenderHeader token={token} t={t} />
          <p className="text-sm text-muted-foreground flex items-center gap-1.5"><Lock className="h-4 w-4" />The sender locked these files. Enter the password they gave you.</p>
          <Input autoFocus type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" data-testid="transfer-password-input" />
          <Button type="submit" disabled={busy || !password.trim()} className="w-full rounded-full h-11" data-testid="transfer-unlock-btn">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Lock className="h-4 w-4 mr-2" />}Unlock
          </Button>
        </form>
      )}
      {error && <p className="mt-4 text-rose-600" data-testid="transfer-error">{error}</p>}
      {t && !t.locked && (
        <div className="space-y-8">
          <div>
            <SenderHeader token={token} t={t} />
            <p className="mt-3 text-sm text-muted-foreground flex items-center gap-1.5" data-testid="transfer-summary">
              <Clock className="h-4 w-4" />{t.files.length} file{t.files.length > 1 ? "s" : ""} · {formatBytes(t.total_size)} · expires {new Date(t.expires_at).toLocaleDateString()}
            </p>
          </div>
          {t.message && <blockquote className="border-l-2 border-amber-500 pl-4 text-base whitespace-pre-wrap" data-testid="transfer-message">{t.message}</blockquote>}
          {images.length > 0 && (
            <div className={`grid gap-3 ${images.length === 1 ? "grid-cols-1 max-w-md" : "grid-cols-2 sm:grid-cols-3"}`} data-testid="transfer-image-grid">
              {images.map((f) => (
                <button key={f.id} onClick={() => setPreview(f)} className="group aspect-square rounded-2xl overflow-hidden border border-border bg-stone-100 dark:bg-stone-900" data-testid={`transfer-thumb-${f.id}`}>
                  <img src={fileUrl(f.id, true)} alt={f.filename} loading="lazy" className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" />
                </button>
              ))}
            </div>
          )}
          <ul className="divide-y divide-border rounded-2xl border border-border">
            {t.files.map((f) => (
              <li key={f.id} className="flex items-center gap-3 p-4" data-testid={`transfer-file-${f.id}`}>
                <FileText className="h-5 w-5 text-amber-600 shrink-0" />
                <div className="flex-1 min-w-0"><div className="truncate text-sm font-medium">{f.filename}</div><div className="text-xs text-muted-foreground">{formatBytes(f.size)}</div></div>
                {canPreview(f) && (
                  <button onClick={() => setPreview(f)} className="h-9 w-9 inline-flex items-center justify-center rounded-full hover:bg-stone-100 dark:hover:bg-stone-800 transition-colors" data-testid={`transfer-preview-${f.id}`} aria-label={`Preview ${f.filename}`}>
                    <Eye className="h-4 w-4" />
                  </button>
                )}
                <a href={fileUrl(f.id)} download={f.filename} className="h-9 w-9 inline-flex items-center justify-center rounded-full hover:bg-stone-100 dark:hover:bg-stone-800 transition-colors" data-testid={`transfer-download-${f.id}`} aria-label={`Download ${f.filename}`}>
                  <Download className="h-4 w-4" />
                </a>
              </li>
            ))}
          </ul>
          {(t.files.length > 1 || photos.length > 1) && (
            <div className="flex flex-wrap gap-2">
              {t.files.length > 1 && (
                <Button onClick={downloadAll} className="rounded-full h-11 px-6" data-testid="transfer-download-all"><Download className="h-4 w-4 mr-2" />Download all</Button>
              )}
              {photos.length > 1 && photosSize <= ZIP_MAX && (
                <Button variant="outline" onClick={downloadPhotosZip} disabled={zipping !== null} className="rounded-full h-11 px-6" data-testid="transfer-photos-zip">
                  {zipping !== null ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Zipping {zipping}%</> : <><FileArchive className="h-4 w-4 mr-2" />All {photos.length} photos (.zip)</>}
                </Button>
              )}
            </div>
          )}
        </div>
      )}
      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-4xl w-[95vw] p-3 sm:p-4" data-testid="transfer-preview-dialog">
          {preview && (
            <>
              <DialogTitle className="text-sm font-medium truncate pr-8">{preview.filename}</DialogTitle>
              <DialogClose className="absolute right-3 top-3 h-8 w-8 inline-flex items-center justify-center rounded-full bg-background/90 border border-border hover:bg-stone-100 dark:hover:bg-stone-800 z-10" data-testid="transfer-preview-close" aria-label="Close preview">
                <X className="h-4 w-4" />
              </DialogClose>
              {preview.content_type === "application/pdf" ? (
                <iframe title={preview.filename} src={fileUrl(preview.id, true)} className="w-full h-[70vh] rounded-xl border border-border bg-white" data-testid="transfer-preview-pdf" />
              ) : (
                <img src={fileUrl(preview.id, true)} alt={preview.filename} className="max-h-[75vh] w-full object-contain rounded-xl" data-testid="transfer-preview-image" />
              )}
              <div className="flex gap-2 justify-end">
                <a href={fileUrl(preview.id, true)} target="_blank" rel="noreferrer" className="inline-flex items-center h-9 px-4 rounded-full border border-border text-sm hover:bg-stone-100 dark:hover:bg-stone-800" data-testid="transfer-preview-open"><ExternalLink className="h-4 w-4 mr-1.5" />Open</a>
                <a href={fileUrl(preview.id)} download={preview.filename} className="inline-flex items-center h-9 px-4 rounded-full bg-primary text-primary-foreground text-sm" data-testid="transfer-preview-download"><Download className="h-4 w-4 mr-1.5" />Download</a>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </PublicShell>
  );
}
